import { createSlice, PayloadAction, createSelector, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '@/store';
import { ChatMessage, ChatConversation, CopilotItem } from '@/types/copilot-chat';
import { ConversationState, ConversationFilters } from '@/types/conversation-store';
import { v4 as uuidv4 } from 'uuid';
import * as copilotHistoryAPI from '@/api/services/copilotHistoryService';
import type {
  CreateConversationRequest,
  AddMessageRequest
} from '@/api/services/copilotHistoryService';

// Constants
const SESSION_STORAGE_KEY = 'copilotSessionState';
const SESSION_EXPIRY_HOURS = 6;

// Extend ChatConversation to include isArchived for our use case
interface ExtendedChatConversation extends ChatConversation {
  isArchived?: boolean;
  userId?: string;
}

// Update the state type to use extended conversation
interface ExtendedConversationState extends Omit<ConversationState, 'conversations' | 'archivedConversations'> {
  conversations: ExtendedChatConversation[];
  archivedConversations: ExtendedChatConversation[];
}

// Simple session storage with expiration
const saveSessionState = (state: Partial<ConversationState>) => {
  try {
    const sessionData = {
      conversations: state.conversations || [],
      archivedConversations: state.archivedConversations || [],
      currentConversationId: state.currentConversationId || null,
      stateVersion: state.stateVersion || 1,
      hasLoadedMockData: false, // Always false since we're not using mock data
      expiresAt: new Date(Date.now() + SESSION_EXPIRY_HOURS * 60 * 60 * 1000).toISOString()
    };
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));
  } catch (e) {
    console.error('Failed to save copilot session state:', e);
  }
};

const loadSessionState = () => {
  try {
    const savedState = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!savedState) return null;
    
    const parsedState = JSON.parse(savedState);
    const expiresAt = new Date(parsedState.expiresAt);
    
    if (expiresAt < new Date()) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }
    
    return {
      conversations: parsedState.conversations || [],
      archivedConversations: parsedState.archivedConversations || [],
      currentConversationId: parsedState.currentConversationId || null,
      stateVersion: parsedState.stateVersion || 1,
      hasLoadedMockData: false // Always false since we're not using mock data
    };
  } catch (e) {
    console.error('Failed to load copilot session state:', e);
    return null;
  }
};

// Initialize state from session storage or defaults
const getInitialState = (): ExtendedConversationState => {
  const sessionState = loadSessionState();
  
  return {
    conversations: sessionState?.conversations || [],
    currentConversationId: sessionState?.currentConversationId || null,
    archivedConversations: sessionState?.archivedConversations || [],
    mockConversations: [],
    searchTerm: '',
    page: 1,
    pageSize: 10,
    totalPages: 1,
    loading: false,
    error: null,
    stateVersion: sessionState?.stateVersion || 1,
    hasLoadedMockData: sessionState?.hasLoadedMockData || false
  };
};

// Async thunks for API operations
// Note: These async thunks are deprecated - use direct API calls instead
export const createConversationAsync = createAsyncThunk(
  'copilotChat/createConversationAsync',
  async (data: CreateConversationRequest, { rejectWithValue }) => {
    try {
      // This needs getToken which isn't available here
      // Use direct API calls in components instead
      console.warn('createConversationAsync is deprecated - use direct API calls');
      return {} as any;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create conversation');
    }
  }
);

// Deprecated - use direct API calls
export const addMessageAsync = createAsyncThunk(
  'copilotChat/addMessageAsync',
  async (data: AddMessageRequest, { rejectWithValue }) => {
    try {
      console.warn('addMessageAsync is deprecated - use direct API calls');
      return {} as any;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to add message');
    }
  }
);

// Deprecated - use direct API calls
export const fetchConversationAsync = createAsyncThunk(
  'copilotChat/fetchConversationAsync',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      console.warn('fetchConversationAsync is deprecated - use direct API calls');
      return {} as any;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch conversation');
    }
  }
);

// Deprecated - use direct API calls
export const archiveConversationAsync = createAsyncThunk(
  'copilotChat/archiveConversationAsync',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      console.warn('archiveConversationAsync is deprecated - use direct API calls');
      return conversationId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to archive conversation');
    }
  }
);

// Deprecated - use direct API calls
export const unarchiveConversationAsync = createAsyncThunk(
  'copilotChat/unarchiveConversationAsync',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      console.warn('unarchiveConversationAsync is deprecated - use direct API calls');
      return conversationId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to unarchive conversation');
    }
  }
);

// Deprecated - use direct API calls
export const deleteConversationAsync = createAsyncThunk(
  'copilotChat/deleteConversationAsync',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      console.warn('deleteConversationAsync is deprecated - use direct API calls');
      return conversationId;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete conversation');
    }
  }
);

const initialState = getInitialState();

export const copilotChatSlice = createSlice({
  name: 'copilotChat',
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<CopilotItem>) => {
      // Find the current conversation and add the message
      const conversation = state.conversations.find(c => c.id === state.currentConversationId);
        if (conversation) {
        conversation.messages = conversation.messages || [];
          conversation.messages.push(action.payload);
        saveSessionState(state);
      }
    },
    
    updateMessageStatus: (state, action: PayloadAction<{ id: string; status: 'sent' | 'delivered' | 'failed' | 'typing' }>) => {
      const { id, status } = action.payload;
      // Find message in current conversation
      const conversation = state.conversations.find(c => c.id === state.currentConversationId);
      if (conversation?.messages) {
        const messageIndex = conversation.messages.findIndex(msg => msg.id === id);
      if (messageIndex !== -1) {
          conversation.messages[messageIndex].status = status;
          saveSessionState(state);
        }
      }
    },
    
    updateMessageWithApiResponse: (state, action: PayloadAction<{ localId: string; apiMessage: CopilotItem }>) => {
      const { localId, apiMessage } = action.payload;
      // Find message in current conversation and replace it with API response
      const conversation = state.conversations.find(c => c.id === state.currentConversationId);
      if (conversation?.messages) {
        const messageIndex = conversation.messages.findIndex(msg => msg.id === localId);
        if (messageIndex !== -1) {
          // Replace the local message with the API response message
          conversation.messages[messageIndex] = apiMessage;
          saveSessionState(state);
        }
      }
    },
    
    setCurrentConversation: (state, action: PayloadAction<string | null | undefined>) => {
      const rawId = action.payload;
      const normalizedId =
        typeof rawId === 'string'
          ? rawId.trim()
          : null;

      state.currentConversationId = normalizedId ? normalizedId : null;
      saveSessionState(state);
    },
    
    clearMessages: (state) => {
      const conversation = state.conversations.find(c => c.id === state.currentConversationId);
        if (conversation) {
          conversation.messages = [];
        saveSessionState(state);
      }
    },
    
    resetState: (state) => {
      state.conversations = [];
      state.archivedConversations = [];
      state.currentConversationId = null;
      state.loading = false;
      state.error = null;
      state.searchTerm = '';
      state.page = 1;
      state.totalPages = 1;
      state.hasLoadedMockData = false;
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    },
    
    loadMockConversations: (state) => {
      // No longer creating mock conversations - just ensure state is properly initialized
      // Mock data will be provided by the backend API
      console.log('Mock conversation loading disabled - using real API data');
    },
    
    // Pagination and search actions
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
      state.page = 1; // Reset to first page when searching
    },
    
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.page = 1; // Reset to first page when changing page size
    },
    
    // Conversation management actions
    archiveConversation: (state, action: PayloadAction<string>) => {
      const conversationIndex = state.conversations.findIndex(c => c.id === action.payload);
      if (conversationIndex !== -1) {
        const conversation = state.conversations[conversationIndex];
        conversation.isArchived = true;
        state.archivedConversations.push(conversation);
        state.conversations.splice(conversationIndex, 1);
        saveSessionState(state);
      }
    },
    
    unarchiveConversation: (state, action: PayloadAction<string>) => {
      const conversationIndex = state.archivedConversations.findIndex(c => c.id === action.payload);
      if (conversationIndex !== -1) {
        const conversation = state.archivedConversations[conversationIndex];
        conversation.isArchived = false;
        state.conversations.push(conversation);
        state.archivedConversations.splice(conversationIndex, 1);
        saveSessionState(state);
      }
    },
    
    deleteConversation: (state, action: PayloadAction<string>) => {
      state.conversations = state.conversations.filter(c => c.id !== action.payload);
      state.archivedConversations = state.archivedConversations.filter(c => c.id !== action.payload);
      if (state.currentConversationId === action.payload) {
        state.currentConversationId = null;
      }
      saveSessionState(state);
    },
    
    // Add mock messages for testing - remove this in production
    addMockMessages: (state, action: PayloadAction<string>) => {
      const conversationId = action.payload;
      const conversation = state.conversations.find(c => c.id === conversationId);
      
      if (conversation) {
        const mockMessages: CopilotItem[] = [
          {
            id: uuidv4(),
            type: 'message',
            content: 'Hello! I\'m here to help you with Pullse. How can I assist you today?',
            sender: {
              id: 'copilot',
              name: 'Pullse Copilot'
            },
            timestamp: new Date(Date.now() - 300000).toISOString(),
            isAI: true,
            createdAt: new Date(Date.now() - 300000).toISOString(),
            status: 'delivered'
          },
          {
            id: uuidv4(),
            type: 'message',
            content: 'Hi! I\'m trying to set up automation workflows. Can you help me get started?',
            sender: {
              id: 'user-1',
              name: 'You'
            },
            timestamp: new Date(Date.now() - 240000).toISOString(),
            isAI: false,
            createdAt: new Date(Date.now() - 240000).toISOString(),
            status: 'delivered'
          },
          {
            id: uuidv4(),
            type: 'message',
            content: 'Of course! I\'d be happy to help you set up automation workflows. Let me show you the basics.',
            sender: {
              id: 'copilot',
              name: 'Pullse Copilot'
            },
            timestamp: new Date(Date.now() - 180000).toISOString(),
            isAI: true,
            createdAt: new Date(Date.now() - 180000).toISOString(),
            status: 'delivered'
          },
          {
            id: uuidv4(),
            type: 'action_card',
            title: 'Automation Workflow Setup',
            summary: 'Here\'s a quick guide to get you started with automation',
            status: 'success',
            timestamp: new Date(Date.now() - 120000).toISOString(),
            payload: {
              step1: 'Create a new workflow',
              step2: 'Add trigger conditions',
              step3: 'Define actions',
              step4: 'Test and activate'
            },
            isCollapsible: true,
            createdAt: new Date(Date.now() - 120000).toISOString(),
            cardType: 'summary'
          },
          {
            id: uuidv4(),
            type: 'message',
            content: 'This looks great! How do I create my first workflow?',
            sender: {
              id: 'user-1',
              name: 'You'
            },
            timestamp: new Date(Date.now() - 60000).toISOString(),
            isAI: false,
            createdAt: new Date(Date.now() - 60000).toISOString(),
            status: 'delivered'
          },
          {
            id: uuidv4(),
            type: 'message',
            content: 'To create your first workflow, go to the Automation section and click "New Workflow". I\'ll guide you through each step!',
            sender: {
              id: 'copilot',
              name: 'Pullse Copilot'
            },
            timestamp: new Date().toISOString(),
            isAI: true,
            createdAt: new Date().toISOString(),
            status: 'delivered'
          }
        ];
        
        conversation.messages = mockMessages;
        conversation.updatedAt = new Date().toISOString();
        saveSessionState(state);
        console.log('Added mock messages to conversation:', conversationId);
      }
    }
  },
  extraReducers: (builder) => {
    // Create conversation async
    builder
      .addCase(createConversationAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createConversationAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.currentConversationId = action.payload.id;
        
        console.log('API Response received:', action.payload);
        
        // Create a new conversation entry using the API response
        const newConversation: ExtendedChatConversation = {
          id: action.payload.id,
          title: action.payload.title,
          messages: action.payload.messages || [],
          createdAt: action.payload.createdAt,
          updatedAt: action.payload.updatedAt,
          isArchived: false,
          userId: 'user-1' // Default userId since it's not in the API response
        };
        
        console.log('Created conversation from API:', newConversation);
        state.conversations.push(newConversation);
        saveSessionState(state);
      })
      .addCase(createConversationAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to create conversation';
      });

    // Add message async
    builder
      .addCase(addMessageAsync.pending, (state) => {
        // Keep existing loading state for message operations
      })
      .addCase(addMessageAsync.fulfilled, (state, action) => {
        // Don't add the message here since we're updating existing messages
        // The message is already added locally and will be updated with API response
        // This prevents duplicate messages
        console.log('Message API call completed, message will be updated via updateMessageWithApiResponse');
      })
      .addCase(addMessageAsync.rejected, (state, action) => {
        state.error = action.payload as string || 'Failed to add message';
      });

    // Fetch conversation async
    builder
      .addCase(fetchConversationAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversationAsync.fulfilled, (state, action) => {
        state.loading = false;
        console.log('Fetched conversation from API:', action.payload);
        
        // Update the conversation with the fetched data
        const conversationIndex = state.conversations.findIndex(c => c.id === action.payload.id);
        if (conversationIndex !== -1) {
          state.conversations[conversationIndex] = {
            ...state.conversations[conversationIndex],
            ...action.payload
          };
          console.log('Updated conversation in state:', state.conversations[conversationIndex]);
        } else {
          // If conversation doesn't exist, create it
          const newConversation: ExtendedChatConversation = {
            id: action.payload.id,
            title: action.payload.title,
            messages: action.payload.messages || [],
            createdAt: action.payload.createdAt,
            updatedAt: action.payload.updatedAt,
            isArchived: false,
            userId: 'user-1'
          };
          state.conversations.push(newConversation);
          console.log('Created new conversation from fetch:', newConversation);
        }
        saveSessionState(state);
      })
      .addCase(fetchConversationAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to fetch conversation';
      });

    // Archive conversation async
    builder
      .addCase(archiveConversationAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(archiveConversationAsync.fulfilled, (state, action) => {
        state.loading = false;
        // Move conversation from active to archived
        const conversationIndex = state.conversations.findIndex(c => c.id === action.payload);
        if (conversationIndex !== -1) {
          const conversation = state.conversations[conversationIndex];
          conversation.isArchived = true;
          state.archivedConversations.push(conversation);
          state.conversations.splice(conversationIndex, 1);
          saveSessionState(state);
        }
      })
      .addCase(archiveConversationAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to archive conversation';
      });

    // Unarchive conversation async
    builder
      .addCase(unarchiveConversationAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(unarchiveConversationAsync.fulfilled, (state, action) => {
        state.loading = false;
        // Move conversation from archived to active
        const conversationIndex = state.archivedConversations.findIndex(c => c.id === action.payload);
        if (conversationIndex !== -1) {
          const conversation = state.archivedConversations[conversationIndex];
          conversation.isArchived = false;
          state.conversations.push(conversation);
          state.archivedConversations.splice(conversationIndex, 1);
          saveSessionState(state);
        }
      })
      .addCase(unarchiveConversationAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to unarchive conversation';
      });

    // Delete conversation async
    builder
      .addCase(deleteConversationAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteConversationAsync.fulfilled, (state, action) => {
        state.loading = false;
        // Remove conversation from both active and archived lists
        state.conversations = state.conversations.filter(c => c.id !== action.payload);
        state.archivedConversations = state.archivedConversations.filter(c => c.id !== action.payload);
        if (state.currentConversationId === action.payload) {
          state.currentConversationId = null;
        }
        saveSessionState(state);
      })
      .addCase(deleteConversationAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'Failed to delete conversation';
      });
  }
});

export const {
  addMessage,
  updateMessageStatus,
  updateMessageWithApiResponse,
  setCurrentConversation,
  clearMessages,
  resetState,
  loadMockConversations,
  setSearchTerm,
  setPage,
  setPageSize,
  archiveConversation,
  unarchiveConversation,
  deleteConversation,
  addMockMessages
} = copilotChatSlice.actions;

// Selectors
export const selectCopilotChatState = (state: RootState) => state.copilotChat;
const EMPTY_MESSAGES: never[] = [];
export const selectMessages = (state: RootState) => {
  const conversation = state.copilotChat.conversations.find(c => c.id === state.copilotChat.currentConversationId);
  return conversation?.messages || EMPTY_MESSAGES;
};
export const selectCurrentConversationId = (state: RootState) => state.copilotChat.currentConversationId;
export const selectIsLoading = (state: RootState) => state.copilotChat.loading;

// Pagination and search selectors
export const selectSearchTerm = (state: RootState) => state.copilotChat.searchTerm;
export const selectPage = (state: RootState) => state.copilotChat.page;
export const selectPageSize = (state: RootState) => state.copilotChat.pageSize;

// Filtered conversations selector
export const selectFilteredConversations = createSelector(
  [(state: RootState) => state.copilotChat.conversations, selectSearchTerm],
  (conversations, searchTerm) => {
    if (!searchTerm) return conversations;
    return conversations.filter(conversation =>
      conversation.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }
);

// Paginated conversations selector
export const selectPaginatedConversations = createSelector(
  [selectFilteredConversations, selectPage, selectPageSize],
  (conversations, page, pageSize) => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return conversations.slice(startIndex, endIndex);
  }
);

// Conversation metrics selector
export const selectConversationMetrics = createSelector(
  [selectMessages, selectCurrentConversationId, (state: RootState) => state.copilotChat.conversations],
  (messages, currentConversationId, conversations) => {
    const totalMessages = messages.length;
    const hasActiveConversation = !!currentConversationId;
    const totalConversations = conversations.length;
    
    return {
      totalConversations,
      totalMessages,
      hasActiveConversation
    };
  }
);

export default copilotChatSlice.reducer;
